import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  CardMedia,
  Button,
  Divider,
  Checkbox,
  Menu,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { observer } from "mobx-react-lite";
import { useStore } from "../../app/stores/store";
import CartPanel from "../cart/Index";
import { toast } from "react-toastify";
import { baseApiUrl } from "../../app/api/constants";
import axios from "axios";
import CategoryFilter from "../../app/interfaces/filters";
import { motion } from "framer-motion";
import ProductSkimmer from "../../app/shared/common/Loader";
import notfound from "../../assets/images/users/product-notfound.png";
import filterIcon from "../../assets/images/users/filter3.png";
import UploadExcels from "../uploadExcel/index";
import { addToCart } from "../../app/services/CartService";
import { getOutlets } from "../../app/services/OutletService";
import { ApiResponse } from "../../app/models/ApiResponse";
import CartQuantityManager from "../cart/QuantityInput";

const ProductPage: React.FC = () => {
  const { cartStore, productStore, outletStore } = useStore();
  const { selectedOutlet } = outletStore;
  const { filteredproducts, setSearchTerm, loadProducts, loading } = productStore;

  const [filters, setFilters] = useState<CategoryFilter[]>([]);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedFilters, setSelectedFilters] = useState<CategoryFilter[]>([]);
  const [orgName, setOrgName] = useState<string>("");

  const getFilters = async () => {
    try {
      const response = await axios.get(`${baseApiUrl}/category`);
      const result = response.data;
      if (result.status === "success") {
        setFilters(result.data || []);
      } else {
        toast.error(result.message || "Failed to load filters");
      }
    } catch {
      toast.error("Failed to get filters");
    }
  };

  useEffect(() => {
    const fetchOutlets = async () => {
      try {
        const res = await getOutlets();
        if (res.status === "success" && res.data.length > 0) {
          setOrgName(res.data[0].Org?.name || "");
        }
      } catch (err) {
        toast.error("Failed to load outlet.");
      }
    };

    fetchOutlets();
  }, []);

  useEffect(() => {
    loadProducts();
    if (selectedOutlet) {
      cartStore.loadCartItems(selectedOutlet.id);
    }
    getFilters();
  }, [cartStore, loadProducts, outletStore, selectedOutlet]);

  const handleFilterOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const toggleFilter = (category: CategoryFilter) => {
    setSelectedFilters((prev) => {
      const exists = prev.some((f) => f.id === category.id);
      return exists ? prev.filter((f) => f.id !== category.id) : [...prev, category];
    });
  };

  const clearFilters = () => {
    setSelectedFilters([]);
    productStore.setSelectedFilters([]);
  };

  const applyFilters = () => {
    productStore.setSelectedFilters(selectedFilters);
    handleFilterClose();
  };

  const handleAddToCart = async (product: any) => {
    if (!selectedOutlet) {
      toast.error("Please select an outlet first.");
      return;
    }
    const customPrice = getDisplayPrice(product);
    const res = (await addToCart({
      product_id: product?.id,
      outlet_id: selectedOutlet.id,
      quantity: 1,
      custom_price: customPrice,
    })) as ApiResponse<any>;

    if (res.status === "success") {
      cartStore.loadCartItems(selectedOutlet.id);
      toast.success(`${product?.name || "Product"} added to cart`);
    } else {
      toast.error(res.message || "Failed to add to cart");
    }
  };

  const getDisplayPrice = (product: any): string => {
    if (!product) return "0";
    const mapper = product?.product_mappers?.[0];
    const today = new Date();

    if (mapper?.custom_price && mapper.start_date) {
      const start = new Date(mapper.start_date);
      const end = mapper.end_date ? new Date(mapper.end_date) : null;

      if (today >= start && (end === null || today <= end)) {
        return mapper.custom_price;
      }
    }
    return product?.base_price || "0";
  };

  if (loading) return <ProductSkimmer />;

  return (
    <Box sx={{ p: 2, backgroundColor: "#f1f5f8", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography fontSize={18} fontWeight={600}>
          Shop Products for {orgName || "Outlet"}
        </Typography>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Content area */}
      <Box sx={{ display: "flex", flex: 1, overflow: "hidden", gap: 2 }}>
        {/* Left: Product Section */}
        <Box sx={{ flex: 2, display: "flex", flexDirection: "column", height: "calc(100vh - 140px)" }}>
          
          {/* 🔒 Fixed search bar */}
          <Box
            sx={{
              display: "flex",
              gap: 1,
              mb: 1,
              position: "sticky",
              top: 0,
              zIndex: 100,
              backgroundColor: "#f1f5f8",
              pb: 1,
              pt: 1,
            }}
          >
            <IconButton color="primary" onClick={handleFilterOpen}>
              <Box
                component="img"
                src={filterIcon}
                alt="Filter"
                sx={{ width: 38, height: 38 }}
              />
            </IconButton>

            <TextField
              fullWidth
              placeholder="Search"
              size="small"
              inputProps={{ maxLength: 30 }}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* 🧾 Scrollable product list */}
          <Box sx={{ flex: 1, overflowY: "auto", pr: 1 }}>
            {filteredproducts.filter(Boolean).length === 0 ? (
              <Box textAlign="center" mt={4}>
                <img
                  src={notfound}
                  alt="No products found"
                  style={{ width: 150, opacity: 0.5 }}
                />
                <Typography mt={2} fontWeight={500} color="text.secondary">
                  No products found
                </Typography>
              </Box>
            ) : (
              filteredproducts.filter(Boolean).map((product) => {
                if (!product) return null;
                const cartItem = cartStore.cart.find(
                  (item) => item?.product?.id === product.id
                );

                return (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <Card
                      variant="outlined"
                      sx={{ display: "flex", alignItems: "center", p: 1, mb: 1 }}
                    >
                      <CardMedia
                        component="img"
                        image={product?.images?.[0]?.path || notfound}
                        alt={product?.name || "Product"}
                        sx={{
                          width: 90,
                          height: 90,
                          objectFit: "cover",
                          borderRadius: 1,
                        }}
                      />
                      <CardContent sx={{ flex: 1 }}>
                        <Typography fontSize={16} fontWeight={600}>
                          {product?.name || "Unnamed Product"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} color="black">
                          Qty: {parseInt(product?.quantity || 0)}, Weight:{" "}
                          {product?.weight || ""} {product?.unit || ""}
                        </Typography>
                        {product?.description && (
                          <Typography variant="body2" color="text.secondary" mt={1}>
                            {product.description}
                          </Typography>
                        )}
                      </CardContent>

                      <Box textAlign="center" px={2}>
                        <Typography mb={2} mr={-7} fontWeight={600}>
                          ₹ {getDisplayPrice(product)}
                        </Typography>

                        {cartItem ? (
                          <CartQuantityManager cartItem={cartItem} />
                        ) : (
                          <Button
                            variant="outlined"
                            size="small"
                            disabled={!selectedOutlet}
                            sx={{
                              mt: 1,
                              textTransform: "none",
                              color: "#006837",
                              borderColor: "#4da079",
                              borderWidth: 2,
                              "&:hover": {
                                backgroundColor: "#e0f2e9",
                                borderColor: "#2c8c64",
                              },
                            }}
                            onClick={() => handleAddToCart(product)}
                          >
                            Add to Cart
                          </Button>
                        )}
                      </Box>
                    </Card>
                  </motion.div>
                );
              })
            )}
          </Box>
        </Box>

        {/* Right: Fixed Cart Panel */}
        <Box
          sx={{
            flex: 1,
            position: "sticky",
            top: 20,
            alignSelf: "flex-start",
            maxHeight: "calc(100vh - 80px)",
            overflowY: "auto",
          }}
        >
          <CartPanel />
          <UploadExcels />
        </Box>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{ sx: { p: 2, minWidth: 220 } }}
      >
        <Typography fontWeight={600} sx={{ mb: 1 }}>
          Filter Products
        </Typography>

        {filters.filter(Boolean).map((filter) => (
          <Box key={filter.id} display="flex" alignItems="center" mb={1}>
            <Checkbox
              checked={selectedFilters.some((f) => f.id === filter.id)}
              onChange={() => toggleFilter(filter)}
              sx={{
                color: "#00703C",
                "&.Mui-checked": { color: "#00703C" },
              }}
            />
            <Typography>{filter.name}</Typography>
          </Box>
        ))}

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button color="success" onClick={clearFilters}>
            Clear
          </Button>
          <Button
            variant="contained"
            sx={{ backgroundColor: "#00703C" }}
            onClick={applyFilters}
          >
            Apply
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default observer(ProductPage);
